import React, { useEffect, useState } from "react";
import { DroppsVariant, variantSelect, productSelect } from "@/store";
import {
  usePdpActions,
  usePdpSellingPlan,
  usePdpProduct,
  usePdpVariant,
} from "./PdpContext";
import { Button } from "@/components/Button";
import { Radio } from "@mui/material";
import { CartAddLineItem, shopifyCartApi } from "@/services/shopifyCart";
import { useCartUiActions } from "../cart";
import { ToggleButton } from "@/components/ToggleButton";
import { Money } from "@/helpers/Money";

export interface AddToCartProps {
  cartSubmitId?: string;
  addToCartText?: string;
  outOfStockText?: string;
  onAddToCartSuccess?: () => void;
}

const DisplayPrice = ({
  price,
  compareAtPrice,
}: {
  price: Money;
  compareAtPrice?: Money;
}) => {
  return (
    <>
      <span className="mr-1 font-semibold">
        <span className="sr-only">Current price</span>
        {price.format()}
      </span>
      {compareAtPrice && compareAtPrice.asCents() > price.asCents() ? (
        <span className="line-through">
          <span className="sr-only">Regular price</span>
          {compareAtPrice.format()}
        </span>
      ) : null}
    </>
  );
};

function getQuantityDiscountMessage(
  quantity_discount: number,
  default_variant_multiplier: number,
  highestMultiplier: number,
  lowestMultiplier: number,
  isBundle: boolean
) {
  if (typeof default_variant_multiplier === "number") {
    if (isBundle) {
      // Render the original message for the highest multiplier
      return (
        <li className="mt-4 font-bold">
          Plus, an{" "}
          <span className="underline">extra {quantity_discount}% OFF</span> for
          bundling up
        </li>
      );
    }

    if (default_variant_multiplier === highestMultiplier) {
      // Render the original message for the highest multiplier
      return (
        <li className="mt-4 font-bold">
          Plus, an{" "}
          <span className="underline">extra {quantity_discount}% OFF</span> on
          our largest size
        </li>
      );
    } else if (default_variant_multiplier === lowestMultiplier) {
      // No message for the lowest multiplier
      // return null;
    } else {
      // Message for the middle case
      return (
        <li className="mt-4 font-bold">
          Plus, an{" "}
          <span className="underline">extra {quantity_discount}% OFF</span> on
          larger box sizes
        </li>
      );
    }
  }

  // Fallback in case default_variant_multiplier is undefined or not a number
}

// Helper function to get the highest multiplier
function getHighestMultiplier(variants: DroppsVariant[]): number {
  return Math.max(
    ...variants.map(
      (variant) => variant.metafields?.default_variant_multiplier || 0
    )
  );
}

// Helper function to get the lowest multiplier
function getLowestMultiplier(variants: DroppsVariant[]): number {
  return Math.min(
    ...variants.map(
      (variant) => variant.metafields?.default_variant_multiplier || 1
    )
  );
}

const OrderTypeButtonGroup = () => {
  const product = usePdpProduct();
  const highestMultiplier = getHighestMultiplier(product.variants);
  const lowestMultiplier = getLowestMultiplier(product.variants);
  const variant = usePdpVariant();
  const actions = usePdpActions();
  const sellingPlan = usePdpSellingPlan();
  const subscription = variantSelect.orderableSubscription(variant);

  const isBulk = product.title.toLocaleLowerCase().includes("bulk");

  const shippingThreshold = 35;

  if (!subscription) {
    return (
      <p className="body">
        <DisplayPrice
          price={variant.price}
          compareAtPrice={variant.compareAtPrice}
        />{" "}
        (One-Time Purchase)
      </p>
    );
  }
  return (
    <div
      data-id="options-button"
      role="radiogroup"
      aria-label="Select to subscribe or purchase one-time"
      className="grid gap-4"
    >
      <div
        className={`group relative rounded border border-solid px-4 py-3 hover:border-primary hover:bg-primary ${
          sellingPlan
            ? "border-primary bg-primary-50 ring-1 ring-primary"
            : "border-gray-600"
        }`}
      >
        <div
          className={`flex ${
            variant?.package_quantity ? "items-start" : ""
          } justify-between`}
        >
          <div className="flex items-center gap-x-2.5">
            <Radio
              color="primary"
              name="purchase-type"
              id="product-subscribe"
              onChange={() => actions.setSubscriptionPreference(true)}
              checked={!!sellingPlan}
              title="One-time purchase"
              value={"subscribe"}
              className="p-0 group-hover:text-white"
            />
            <label
              htmlFor="product-subscribe"
              className="mb-0 text-base font-semibold text-gray-600 before:absolute before:-inset-1 before:content-[''] group-hover:text-white"
            >
              Subscribe
            </label>
          </div>

          <div className="flex-col text-right">
            <div className="inline-flex gap-x-2 group-hover:text-white">
              <span className={`text-lg ${sellingPlan ? "font-bold" : ""}`}>
                <span className="sr-only">Subscription price</span>
                {subscription.discount.price.format()}
              </span>
            </div>

            {variant?.package_quantity && product?.type !== "Bundle" && (
              <p className="text-sm group-hover:text-white">
                {variant?.package_quantity && variant?.price
                  ? `($${(
                      subscription.discount.price.asCents() /
                      variant.package_quantity /
                      100
                    ).toFixed(2)}/pod)`
                  : null}
              </p>
            )}

            {/* Render the compare_at_price in place of cost per pod if it's a Bundle */}
            {product?.type === "Bundle" && variant.compareAtPrice && (
              <p className="text-lg group-hover:text-white">
                <span className="sr-only">Compare at price</span>
                <span className="text-sm line-through">
                  {variant.compareAtPrice.format()}
                </span>
              </p>
            )}
          </div>
        </div>

        {(() => {
          // Check if the subscription discount price qualifies for free shipping
          const qualifiesForFreeShipping =
            subscription.discount.price.asDollars() > shippingThreshold;
          // Only display discount if the item is not marked as bulk
          const discountText =
            !isBulk &&
            subscription.discount.kind === "percentage" &&
            subscription.discount.percentage > 0
              ? `${subscription.discount.percentage}% off`
              : "";
          const freeShippingText = qualifiesForFreeShipping
            ? "Free Shipping"
            : "";
          const message =
            discountText && freeShippingText
              ? `${discountText} and ${freeShippingText}`
              : discountText || freeShippingText;

          return message ? (
            <p
              className={`body mb-1 ${
                variant?.package_quantity ? "" : "mt-4"
              } font-semibold text-primary-600 group-hover:text-white`}
            >
              {message}
            </p>
          ) : null;
        })()}

        <ul className="pb-2 pl-2 text-sm group-hover:text-white">
          <li className="list-inside list-disc">Ships every 4 months.</li>
          <li className="list-inside list-disc">
            Change, skip, or cancel at any time
          </li>
          {typeof variant?.quantity_discount === "number" &&
            variant.quantity_discount > 0 &&
            getQuantityDiscountMessage(
              variant.quantity_discount,
              variant.default_variant_multiplier,
              highestMultiplier,
              lowestMultiplier,
              product?.type === "Bundle"
            )}
        </ul>
      </div>
      <div
        className={`group relative rounded border border-solid px-4 py-3 hover:border-primary hover:bg-primary ${
          !sellingPlan
            ? "border-primary bg-primary-50 ring-1 ring-primary"
            : "border-gray-600"
        }`}
      >
        <div className="flex justify-between">
          <div className="flex items-center gap-x-2.5">
            <Radio
              color="primary"
              name="purchase-type"
              id="product-onetime"
              onChange={() => actions.setSubscriptionPreference(false)}
              checked={!sellingPlan}
              title="One-time purchase"
              value={"onetime"}
              className="p-0 group-hover:text-white"
            />
            <label
              htmlFor="product-onetime"
              className="mb-0 text-base font-semibold text-gray-600 before:absolute before:-inset-1 before:content-[''] group-hover:text-white"
            >
              One-time purchase
            </label>
          </div>
          {variant?.package_quantity ? (
            <span className="flex flex-col text-right text-lg group-hover:text-white">
              <span className="sr-only">Current price</span>
              <span className={`${!sellingPlan ? "font-bold" : ""}`}>
                {variant.price.format()}
              </span>
              {product?.type !== "Bundle" && (
                <span className="text-sm group-hover:text-white">
                  {variant?.package_quantity && variant?.price
                    ? `($${(
                        variant.price.asCents() /
                        variant.package_quantity /
                        100
                      ).toFixed(2)}/pod)`
                    : null}
                </span>
              )}
              {product?.type === "Bundle" && variant.compareAtPrice && (
                <span className="group-hover:text-white">
                  <span className="sr-only">Compare at price</span>
                  <span className="text-sm line-through">
                    {variant.compareAtPrice.format()}
                  </span>
                </span>
              )}
            </span>
          ) : (
            <span className="group-hover:text-white">
              <span className="sr-only">Current price</span>
              <span className={`${!sellingPlan ? "font-bold" : ""}`}>
                {variant.price.format()}
              </span>
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

// VariantButton component
const VariantButton = ({
  variant,
  selected,
  className,
  labelClassName,
}: {
  variant: DroppsVariant;
  selected: boolean;
  className?: string;
  labelClassName?: string;
}) => {
  const actions = usePdpActions();

  return (
    <div
      data-id="variants-button"
      key={variant.id}
      className={`flex-1 ${className}`}
    >
      {variant.quantity_discount !== undefined &&
        (variant.quantity_discount ?? 0) > 0 && (
          <p className={`buy-more-save-more-label ${labelClassName}`}>
            Save {variant.quantity_discount ?? 0}%
          </p>
        )}

      <ToggleButton
        className="w-full"
        selected={selected}
        size="md"
        onClick={() => actions.selectVariant(variant)}
        label={
          <span className="text-base">{variantSelect.sizeFormat(variant)}</span>
        }
        name="variant-button"
      />
    </div>
  );
};

export const VariantSizeOptionsGroup = () => {
  const product = usePdpProduct();
  const selectedVariant = usePdpVariant();
  const defaultVariant = productSelect.defaultVariant(product);

  if (product.variants.length === 1) {
    return (
      <p className="body">
        <span className="font-semibold">Size: </span>
        {variantSelect.sizeFormat(defaultVariant)}
      </p>
    );
  } else {
    return (
      <div
        role="radiogroup"
        className="variant-option-buttons flex space-x-2"
        aria-label={`Select product ${defaultVariant.size.unit ?? ""}`}
      >
        {product.variants.map((x) => (
          <VariantButton
            key={variantSelect.reactKey(x)}
            variant={x}
            selected={x.id == selectedVariant.id}
            className={x.id == selectedVariant.id ? "bg-primary-50" : ""}
            labelClassName={x.id == selectedVariant.id ? "bg-primary-800" : ""}
          />
        ))}
      </div>
    );
  }
};

export const AddToCart = ({
  cartSubmitId,
  addToCartText = "Add To Cart",
  outOfStockText = "Out of Stock",
}: AddToCartProps) => {
  const shopifyCartAdder = shopifyCartApi.useTrackedAdd();
  const cartActions = useCartUiActions();
  const variant = usePdpVariant();
  const subscription = variantSelect.orderableSubscription(variant);
  const sellingPlan = usePdpSellingPlan();
  const product = usePdpProduct();
  const isBundle =
    product.bundledItems &&
    product.bundledItems.length > 0 &&
    !product.handle.includes("starter-kit");

  return (
    <div>
      <VariantSizeOptionsGroup />
      <div className="pt-4">
        <OrderTypeButtonGroup />
      </div>
      <div className="mt-4 basis-full pt-4">
        <Button
          id={cartSubmitId}
          className={`flex w-full bg-green-500 text-primary not-disabled:hover:bg-green-900 not-disabled:hover:text-white`}
          size="lg"
          disabled={!variant.available}
          onClick={() => {
            const lineItem: CartAddLineItem[] = [
              variantSelect.cartAddLineItem(variant, {
                subscription: sellingPlan,
              }),
            ];
            const sourceModule = isBundle
              ? "bundle_pdp_add_to_cart"
              : "pdp_add_to_cart";
            const body = { items: lineItem, sourceModule };
            shopifyCartAdder.mutate(body, {
              onSuccess: () => cartActions.openDrawer(),
            });
          }}
        >
          <div className="flex w-full items-center justify-between">
            <span>{variant.available ? addToCartText : outOfStockText}</span>
            <span>
              {sellingPlan
                ? subscription?.discount.price.format()
                : variant.price.format()}
            </span>
          </div>
        </Button>
      </div>
      {!variant.available && (
        <div className="basis-full pt-4">
          <Button
            id="BIS_trigger"
            className="flex w-full"
            aria-label="notify me when restocked"
            size="lg"
          >
            Notify Me When Restocked
          </Button>
        </div>
      )}
    </div>
  );
};
